import { z } from 'zod';
import { CadFingerprintSchema } from './cadImport.js';
import { SEMVER_RE } from './semver.js';

/**
 * Optomech parts catalog — spec
 * docs/superpowers/specs/2026-10-19-optomech-catalog-composition-design.md
 * §4.1–§4.3 (migration 125).
 *
 * The catalog holds DIMENSIONS, not shapes: a row of a standard family is
 * built into a solid by fea-service's generator for that family
 * (fea-service/fea/parts.py), so every number here is one the generator or a
 * fit rule reads. Lengths are millimetres, the lens units of the rest of the
 * optomech contract; an inch drawing is converted by the seed compiler and the
 * conversion is recorded in the dimension's citation.
 *
 * Each family schema is split into GENERATOR INPUTS (required: a row missing
 * one cannot be built, and a row that cannot be built is not a part) and FIT
 * DATA (optional: the row loads, and a fit rule that needs the field refuses
 * by name). The split is the schema's required/optional, not a side list.
 */

export const OPTOMECH_PART_FAMILIES = ['lens_tube', 'retaining_ring', 'spacer_ring', 'cell', 'adapter', 'other'] as const;
export const OptomechPartFamilySchema = z.enum(OPTOMECH_PART_FAMILIES);
export type OptomechPartFamily = z.infer<typeof OptomechPartFamilySchema>;

/** The families a generator exists for; the others load as rows but cannot be composed yet. */
export const OPTOMECH_GENERATED_FAMILIES = ['lens_tube', 'retaining_ring', 'spacer_ring'] as const;
export type OptomechGeneratedFamily = typeof OPTOMECH_GENERATED_FAMILIES[number];

export const OPTOMECH_PART_SCOPES = ['global', 'project'] as const;
export const OptomechPartScopeSchema = z.enum(OPTOMECH_PART_SCOPES);

/** A thread key as the vendor names the standard: SM05, SM1, SM1.5, SM2, M25.5x0.5 as they arrive. */
export const OPTOMECH_THREAD_KEY_RE = /^[A-Za-z0-9.]{1,20}$/;
export const OptomechThreadKeySchema = z.string().regex(OPTOMECH_THREAD_KEY_RE);

/** One thread standard (optomech_threads). Fit compares `key`; the diameters model the interface and sanity-check seeds. */
export const OptomechThreadSchema = z.object({
  key: OptomechThreadKeySchema,
  major_diameter_mm: z.number().positive(),
  pitch_mm: z.number().positive(),
  /** The internal thread's minimum minor diameter: the one cylinder every threaded interface is modelled at (spec §7, §11 Q2). */
  interface_diameter_mm: z.number().positive(),
  /** 60° unified form, or metric. */
  form: z.enum(['UN', 'M']),
  /** The vendor's callout as written, e.g. 1.035"-40. */
  callout: z.string().min(1).max(60),
  source: z.string().min(1),
});
export type OptomechThread = z.infer<typeof OptomechThreadSchema>;

const mm = z.number().positive().finite();

/**
 * A lens tube: a cylinder with a bore the optic drops into, internally
 * threaded. The generator models the threaded bore as the thread's
 * MINOR-diameter cylinder, which is `bore_mm` (spec §7, §11 Q2): a retaining
 * ring generated at the same diameter then shares that cylinder with the tube
 * in the mesh.
 */
export const OptomechLensTubeInterfaceSchema = z.object({
  // generator inputs
  thread: OptomechThreadKeySchema,
  bore_mm: mm,
  outer_diameter_mm: mm,
  length_mm: mm,
  // fit data
  /** Internally threaded length measured from each end; absent when the drawing does not state it. */
  internal_thread_length_mm: z.object({ front: mm.optional(), rear: mm.optional() }).strict().optional(),
  /** An external thread on one end, modelled as a stub of `diameter_mm` over `length_mm`. The stub takes the thread's MAJOR diameter, not the interface diameter the bore takes: a tube threaded inside and outside at one standard would otherwise have no wall there. Changes the solid when present. */
  external_thread: z.object({ thread: OptomechThreadKeySchema, diameter_mm: mm, length_mm: mm, end: z.enum(['front', 'rear']) }).strict().optional(),
  /** An internal stop the optic can seat on: its rear face's position from the tube's rear face, its thickness and its inner diameter. Changes the solid when present. */
  shoulder: z.object({ from_rear_mm: z.number().nonnegative().finite(), thickness_mm: mm, inner_diameter_mm: mm }).strict().optional(),
}).strict().refine((t) => t.bore_mm < t.outer_diameter_mm, { message: 'bore_mm must be smaller than outer_diameter_mm' })
  .refine((t) => t.shoulder === undefined || (t.shoulder.from_rear_mm + t.shoulder.thickness_mm <= t.length_mm && t.shoulder.inner_diameter_mm < t.bore_mm), { message: 'a shoulder lies inside the tube and inside the bore' })
  .refine((t) => t.external_thread === undefined || (t.external_thread.length_mm < t.length_mm && t.external_thread.diameter_mm > t.bore_mm && t.external_thread.diameter_mm < t.outer_diameter_mm), { message: 'an external thread stub is shorter than the tube and lies between the bore and the outer diameter' });
export type OptomechLensTubeInterface = z.infer<typeof OptomechLensTubeInterfaceSchema>;

/**
 * A retaining ring: an annulus that threads into a tube and bears on the
 * optic with one face. `outer_diameter_mm` is the thread's minor diameter as
 * the drawing gives it, i.e. the bore of the tube it threads into.
 */
export const OptomechRetainingRingInterfaceSchema = z.object({
  // generator inputs
  thread: OptomechThreadKeySchema,
  outer_diameter_mm: mm,
  thickness_mm: mm,
  clear_aperture_mm: mm,
  // fit data
  /** Which face bears on the optic, and the chamfer angle of that face when it is not flat. */
  seat: z.object({ face: z.enum(['front', 'rear']), chamfer_deg: z.number().positive().lt(90).optional() }).strict().optional(),
}).strict().refine((r) => r.clear_aperture_mm < r.outer_diameter_mm, { message: 'clear_aperture_mm must be smaller than outer_diameter_mm' });
export type OptomechRetainingRingInterface = z.infer<typeof OptomechRetainingRingInterfaceSchema>;

/** A spacer ring: an annulus that rides in a tube between two optics. */
export const OptomechSpacerRingInterfaceSchema = z.object({
  // generator inputs
  outer_diameter_mm: mm,
  inner_diameter_mm: mm,
  length_mm: mm,
  // fit data
  seats: z.object({
    front: z.object({ chamfer_deg: z.number().positive().lt(90).optional() }).strict().optional(),
    rear: z.object({ chamfer_deg: z.number().positive().lt(90).optional() }).strict().optional(),
  }).strict().optional(),
}).strict().refine((s) => s.inner_diameter_mm < s.outer_diameter_mm, { message: 'inner_diameter_mm must be smaller than outer_diameter_mm' });
export type OptomechSpacerRingInterface = z.infer<typeof OptomechSpacerRingInterfaceSchema>;

/** The dimension record of each generated family, by family. */
export const OPTOMECH_FAMILY_INTERFACE = {
  lens_tube: OptomechLensTubeInterfaceSchema,
  retaining_ring: OptomechRetainingRingInterfaceSchema,
  spacer_ring: OptomechSpacerRingInterfaceSchema,
} as const satisfies Record<OptomechGeneratedFamily, z.ZodTypeAny>;

/** How a row's solid comes to be. A stored-STEP kind is reserved (spec §11) and not accepted yet. */
export const OptomechPartGeometrySchema = z.object({
  kind: z.literal('parametric'),
  /** The generator (fea-service/fea/parts.py) the row is built by: its family's. */
  generator: z.enum(OPTOMECH_GENERATED_FAMILIES),
  /** That generator's version when the row was written (`GENERATOR_VERSIONS` there); a change to what a generator does is a new version, never an edit of the old one. */
  generator_version: z.string().regex(SEMVER_RE),
}).strict();
export type OptomechPartGeometry = z.infer<typeof OptomechPartGeometrySchema>;

/** Where one dimension was read: the document, the cell in it, its URL and the date read. */
export const OptomechCitationSchema = z.object({
  document: z.string().min(1),
  locator: z.string().min(1),
  url: z.string().url().optional(),
  retrieved: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Required when the stored number is not the cell verbatim (an inch value converted to millimetres). */
  note: z.string().min(1).optional(),
}).strict();
export type OptomechCitation = z.infer<typeof OptomechCitationSchema>;

export const OPTOMECH_PART_BODY_ROLES = ['structural', 'bonded_insert'] as const;

/** One solid of a part version, with the material it is made of by stable id (spec rule 6). */
export const OptomechPartBodySchema = z.object({
  body_name: z.string().regex(/^[A-Za-z0-9_-]{1,60}$/),
  role: z.enum(OPTOMECH_PART_BODY_ROLES),
  material_id: z.string().uuid(),
  generator_body: z.string().regex(/^[A-Za-z0-9_-]{1,60}$/),
});
export type OptomechPartBody = z.infer<typeof OptomechPartBodySchema>;

/** One part VERSION as the API serves it, with its bodies. `interface` is validated per `family` by the route. */
export const OptomechPartRowSchema = z.object({
  /** The version id: what parts_used names. */
  id: z.string().uuid(),
  /** Stable across versions: what a composition names. */
  part_id: z.string().uuid(),
  version: z.number().int().positive(),
  scope: OptomechPartScopeSchema,
  project_id: z.string().uuid().nullable(),
  vendor: z.string(),
  part_number: z.string(),
  revision: z.string(),
  name: z.string(),
  family: OptomechPartFamilySchema,
  interface: z.record(z.string(), z.unknown()),
  /** Keyed by the dotted field path in `interface` (`bore_mm`, `shoulder.inner_diameter_mm`). */
  citations: z.record(z.string(), OptomechCitationSchema),
  geometry: OptomechPartGeometrySchema,
  fingerprint: CadFingerprintSchema.nullable(),
  source_url: z.string().nullable(),
  drawing_url: z.string().nullable(),
  bodies: z.array(OptomechPartBodySchema),
  created_by: z.string(),
  created_at: z.string(),
  superseded_at: z.string().nullable(),
});
export type OptomechPartRow = z.infer<typeof OptomechPartRowSchema>;

export const OptomechThreadListSchema = z.object({ threads: z.array(OptomechThreadSchema) });

// --- fit (spec §5) -----------------------------------------------------------

export const OPTOMECH_FIT_VERDICTS = ['fits', 'fits_with_note', 'does_not_fit'] as const;
export const OptomechFitVerdictSchema = z.enum(OPTOMECH_FIT_VERDICTS);
export type OptomechFitVerdict = z.infer<typeof OptomechFitVerdictSchema>;

/**
 * One fit judgement: the verdict and the named facts it rests on
 * (`clearance_mm`, `thread_match`, `length_margin_mm`, …), with each reason a
 * verdict short of `fits` was given. Fit never selects; it only judges. The
 * clearance a lens rule used is always among the facts as
 * `diametral_clearance_mm` (the sum of both sides), never silent.
 */
export const OptomechFitResultSchema = z.object({
  verdict: OptomechFitVerdictSchema,
  facts: z.record(z.string(), z.union([z.number(), z.string(), z.boolean()])),
  reasons: z.array(z.string()),
});
export type OptomechFitResult = z.infer<typeof OptomechFitResultSchema>;

/** The lens a search judged parts against, as the fit rules saw it. */
export const OptomechFitTargetSchema = z.object({
  /** The element's first and last surface numbers in the current system: a singlet's front and rear, a cemented group's front and its last face. */
  surfaces: z.tuple([z.number().int(), z.number().int()]),
  /** Twice the semi-diameter all the element's surfaces share: the element's one edge diameter. */
  lens_diameter_mm: z.number().positive(),
  center_thickness_mm: z.number().positive(),
  edge_thickness_mm: z.number().positive(),
  /** The user's number (spec §11 Q3: the family defaults have no source yet). */
  diametral_clearance_mm: z.number().nonnegative(),
});
export type OptomechFitTarget = z.infer<typeof OptomechFitTargetSchema>;

/**
 * A part in a search result: the row, and when the search named a lens, its
 * verdict against that lens, its place in the ranking of the parts that hold
 * (spec §5: shortest length that holds; null for one that does not, and for a
 * family no rule judges against a lens) and the sentence that says why.
 */
export const OptomechPartCandidateSchema = OptomechPartRowSchema.extend({
  fit: OptomechFitResultSchema.nullable(),
  rank: z.number().int().positive().nullable(),
  rationale: z.string().nullable(),
});
export type OptomechPartCandidate = z.infer<typeof OptomechPartCandidateSchema>;

/** GET /optomech/parts: the current global rows that pass the filters, judged and ranked when `fits_surface` was given. */
export const OptomechPartSearchSchema = z.object({
  parts: z.array(OptomechPartCandidateSchema),
  fit_target: OptomechFitTargetSchema.nullable(),
});
export type OptomechPartSearch = z.infer<typeof OptomechPartSearchSchema>;
